import { create } from 'zustand';

import { useOAuthStore } from '@/features/auth/store';
import { useBrowserWalletStore } from '@/features/browserWallet/store';
import type { MeResponse } from '@/lib/api/auth';
import {
    fetchSnappyAdmission,
    fetchSnappyUser,
    type SnappyAdmission,
    type SnappyUser,
} from '@/lib/api/snappy';

import {
    computeAvailability,
    NO_SOCIAL_AVAILABILITY,
    v1AddressOf,
} from './availability';
import { readFlowFlags } from './flagsStore';
import type { FlowAvailability, TradingFlow } from './types';

/** The flow picked in this tab session: `{ accountHash, flow }`. */
export const FLOW_TAB_PICK_KEY = 'snappy.flow.active.v1';
/** The browser wallet dialog already opened by itself in this tab session. */
export const FLOW_V2_AUTO_OPENED_KEY = 'snappy.flow.v2DialogShown.v1';
export const FLOW_RESOLVE_TIMEOUT_MS = 5_000;

interface FlowDependencies {
    fetchSnappyUser: () => Promise<SnappyUser>;
    fetchAdmission: () => Promise<SnappyAdmission>;
    /** The flow switch is a full reload (plan §3.2.1). */
    reload: () => void;
    timeoutMs: number;
}

const defaultDependencies: FlowDependencies = {
    fetchSnappyUser: async () =>
        fetchSnappyUser({ timeoutMs: FLOW_RESOLVE_TIMEOUT_MS, retry: 0 }),
    fetchAdmission: async () =>
        fetchSnappyAdmission({ timeoutMs: FLOW_RESOLVE_TIMEOUT_MS, retry: 0 }),
    reload: () => window.location.reload(),
    timeoutMs: FLOW_RESOLVE_TIMEOUT_MS,
};

let dependencies: FlowDependencies = defaultDependencies;

/** Test seam: replace any dependency; call with nothing to restore. */
export function setFlowDependencies(
    overrides?: Partial<FlowDependencies>,
): void {
    dependencies = overrides
        ? { ...defaultDependencies, ...overrides }
        : defaultDependencies;
}

interface TabPick {
    accountHash: string;
    flow: TradingFlow;
}

function readTabPick(): TabPick | null {
    try {
        const raw = sessionStorage.getItem(FLOW_TAB_PICK_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<TabPick>;
        if (
            typeof parsed.accountHash === 'string' &&
            (parsed.flow === 'v1' || parsed.flow === 'v2')
        ) {
            return {
                accountHash: parsed.accountHash.toLowerCase(),
                flow: parsed.flow,
            };
        }
        return null;
    } catch {
        return null;
    }
}

function writeTabPick(pick: TabPick): void {
    try {
        sessionStorage.setItem(FLOW_TAB_PICK_KEY, JSON.stringify(pick));
    } catch {
        // without storage the pick lasts until the next reload only.
    }
}

function removeSessionKey(key: string): void {
    try {
        sessionStorage.removeItem(key);
    } catch {
        // nothing to clear when storage is unavailable.
    }
}

function v2DialogAlreadyShown(): boolean {
    try {
        return sessionStorage.getItem(FLOW_V2_AUTO_OPENED_KEY) === '1';
    } catch {
        return false;
    }
}

function markV2DialogShown(): void {
    try {
        sessionStorage.setItem(FLOW_V2_AUTO_OPENED_KEY, '1');
    } catch {
        // at worst the dialog opens by itself once more.
    }
}

async function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(
            () => reject(new Error('flow resolution request timed out')),
            ms,
        );
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (error: unknown) => {
                clearTimeout(timer);
                reject(error);
            },
        );
    });
}

// ── module-private resolution bookkeeping ───────────────────────────────
// Bumped by every new resolution and by reset/onSessionLost: a continuation
// that finds it changed belongs to a session that is gone and writes nothing.
let generation = 0;
let inflight: {
    accountHash: string;
    promise: Promise<TradingFlow | null>;
} | null = null;
let pendingChoice: ((flow: TradingFlow | null) => void) | null = null;
// the last good answers for the current account: a failed refresh keeps them
// instead of reading as "no backups, not admitted".
let lastAnswers: {
    accountHash: string;
    snappyUser: SnappyUser | null;
    admission: SnappyAdmission | null;
} | null = null;

function settlePendingChoice(flow: TradingFlow | null): void {
    const settle = pendingChoice;
    pendingChoice = null;
    settle?.(flow);
}

async function fetchAvailability(
    me: MeResponse,
    accountHash: string,
): Promise<FlowAvailability> {
    const [user, admission] = await Promise.allSettled([
        withTimeout(dependencies.fetchSnappyUser(), dependencies.timeoutMs),
        withTimeout(dependencies.fetchAdmission(), dependencies.timeoutMs),
    ]);
    const previous =
        lastAnswers?.accountHash === accountHash ? lastAnswers : null;
    const answers = {
        accountHash,
        snappyUser:
            user.status === 'fulfilled'
                ? user.value
                : (previous?.snappyUser ?? null),
        admission:
            admission.status === 'fulfilled'
                ? admission.value
                : (previous?.admission ?? null),
    };
    lastAnswers = answers;
    return computeAvailability({
        me,
        snappyUser: answers.snappyUser,
        admission: answers.admission,
        flags: readFlowFlags(accountHash),
        heldAddress: useBrowserWalletStore.getState().address,
    });
}

interface FlowState {
    /** null only while a social identity is being resolved (or after a sign-out). */
    active: TradingFlow | null;
    availability: FlowAvailability | null;
    resolving: boolean;
    choiceOpen: boolean;
    /** The (lowercased) social identity the state above belongs to. */
    accountHash: string | null;
    /**
     * Decides the flow for a `/v2/auth/me` answer (plan §2, §3.2).
     *
     * No social session: synchronous, no request; `v2` only when a key is held
     * here (§4.3), else `v1`. Social: the backups and the launch admission are
     * read in parallel (5 s each, a failure counts as "not available"); both
     * flows → the tab-session pick, or the choice dialog (the promise settles
     * when the user picks); one → that one; none → `v1`. Idempotent per
     * accountHash. Settles with null when the session ends before a flow was
     * decided, which callers treat as "do nothing".
     */
    resolve: (me: MeResponse | null) => Promise<TradingFlow | null>;
    /** The choice dialog's answer; remembered for this tab session. */
    choose: (flow: TradingFlow) => void;
    /** The header toggle: remember the pick, then reload (§3.2.1). */
    switchTo: (flow: TradingFlow) => void;
    /** Re-read availability after a redeem or a backup change. */
    refreshAvailability: () => Promise<FlowAvailability | null>;
    /**
     * The social session ended without a Sign out (a 401, a sign-out in
     * another tab). A held key keeps V2 (the key is the identity, §4.3).
     */
    onSessionLost: () => void;
    /** Sign out: everything back to "not resolved". */
    reset: () => void;
}

export const useFlowStore = create<FlowState>((set, get) => {
    /**
     * Entering V2: boot the browser wallet; when it stays locked for a
     * signed-in account, open its dialog once per tab session (restore when
     * the account has backups, else set up).
     */
    const enterV2 = async (withSocial: boolean): Promise<void> => {
        const entered = generation;
        try {
            await useBrowserWalletStore.getState().boot();
        } catch {
            // the banner shows whatever state boot() left behind.
        }
        if (!withSocial) return;
        if (entered !== generation || get().active !== 'v2') return;
        const wallet = useBrowserWalletStore.getState();
        if (wallet.signer || wallet.userError || wallet.dialogOpen) return;
        if (v2DialogAlreadyShown()) return;
        markV2DialogShown();
        const backups =
            wallet.user?.backups ?? lastAnswers?.snappyUser?.backups ?? [];
        wallet.open(backups.length > 0 ? 'restore' : 'create');
    };

    const activate = (flow: TradingFlow, withSocial: boolean) => {
        set({ active: flow });
        if (flow === 'v2') void enterV2(withSocial);
    };

    return {
        active: null,
        availability: null,
        resolving: false,
        choiceOpen: false,
        accountHash: null,

        resolve: async (me) => {
            if (!me?.authenticated || !me.accountHash) {
                generation += 1;
                inflight = null;
                settlePendingChoice(null);
                const held = useBrowserWalletStore.getState().address;
                const flow: TradingFlow = held ? 'v2' : 'v1';
                set({
                    availability: held ? null : NO_SOCIAL_AVAILABILITY,
                    resolving: false,
                    choiceOpen: false,
                    accountHash: null,
                });
                activate(flow, false);
                return flow;
            }

            const accountHash = me.accountHash.toLowerCase();
            if (inflight?.accountHash === accountHash) return inflight.promise;
            const current = get();
            if (
                current.accountHash === accountHash &&
                current.active !== null
            ) {
                return current.active;
            }

            generation += 1;
            const started = generation;
            inflight = null;
            settlePendingChoice(null);

            // a pick is only ever written while both flows were available.
            const pick = readTabPick();
            if (pick?.accountHash === accountHash) {
                set({
                    accountHash,
                    availability: {
                        v1: true,
                        v2: true,
                        v1Address: v1AddressOf(me),
                        v2Address: useBrowserWalletStore.getState().address,
                    },
                    resolving: true,
                    choiceOpen: false,
                });
                activate(pick.flow, true);
                void fetchAvailability(me, accountHash).then((availability) => {
                    if (started !== generation) return;
                    set({ availability, resolving: false });
                });
                return pick.flow;
            }

            set({
                active: null,
                accountHash,
                availability: null,
                resolving: true,
                choiceOpen: false,
            });
            const promise = (async (): Promise<TradingFlow | null> => {
                const availability = await fetchAvailability(me, accountHash);
                if (started !== generation) return null;
                if (availability.v1 && availability.v2) {
                    const chosen = new Promise<TradingFlow | null>((settle) => {
                        pendingChoice = settle;
                    });
                    set({ availability, resolving: false, choiceOpen: true });
                    return chosen;
                }
                const flow: TradingFlow =
                    availability.v2 && !availability.v1 ? 'v2' : 'v1';
                set({ availability, resolving: false });
                activate(flow, true);
                return flow;
            })().finally(() => {
                if (inflight?.promise === promise) inflight = null;
            });
            inflight = { accountHash, promise };
            return promise;
        },

        choose: (flow) => {
            const { accountHash } = get();
            if (accountHash) writeTabPick({ accountHash, flow });
            set({ choiceOpen: false });
            activate(flow, true);
            settlePendingChoice(flow);
        },

        switchTo: (flow) => {
            const { accountHash, active } = get();
            if (!accountHash || flow === active) return;
            writeTabPick({ accountHash, flow });
            // an explicit switch to V2 always gets its dialog once.
            if (flow === 'v2') removeSessionKey(FLOW_V2_AUTO_OPENED_KEY);
            dependencies.reload();
        },

        refreshAvailability: async () => {
            const me = useOAuthStore.getState().me;
            const accountHash = get().accountHash;
            if (
                !me?.authenticated ||
                !me.accountHash ||
                me.accountHash.toLowerCase() !== accountHash
            ) {
                return get().availability;
            }
            const started = generation;
            const availability = await fetchAvailability(me, accountHash);
            if (started !== generation) return get().availability;
            set({ availability });
            return availability;
        },

        onSessionLost: () => {
            generation += 1;
            inflight = null;
            lastAnswers = null;
            removeSessionKey(FLOW_TAB_PICK_KEY);
            const keepV2 =
                get().active === 'v2' &&
                !!useBrowserWalletStore.getState().address;
            set({
                active: keepV2 ? 'v2' : null,
                availability: null,
                resolving: false,
                choiceOpen: false,
                accountHash: null,
            });
            settlePendingChoice(null);
        },

        reset: () => {
            generation += 1;
            inflight = null;
            lastAnswers = null;
            removeSessionKey(FLOW_TAB_PICK_KEY);
            removeSessionKey(FLOW_V2_AUTO_OPENED_KEY);
            set({
                active: null,
                availability: null,
                resolving: false,
                choiceOpen: false,
                accountHash: null,
            });
            settlePendingChoice(null);
        },
    };
});

/**
 * Whether the V1/V2 toggle is offered. The flow in use counts as unlocked:
 * a social user with no evidence runs V1 by default, and after redeeming a V2
 * code the view tells them to click the V1 button, so it has to be there.
 */
export function selectBothFlowsUnlocked(state: FlowState): boolean {
    const { active, availability } = state;
    if (active === null || availability === null) return false;
    return (
        (availability.v1 || active === 'v1') &&
        (availability.v2 || active === 'v2')
    );
}
